import { useRef, useState, useCallback } from 'react';
import useEscapeKey from './useEscapeKey';
import { unprojectToPlane, SCENE_VIEW } from '../engine/battlefield/projection';
import { moveToken } from '../engine/battlefield/tokens';
import { surfaceHeightAt } from '../engine/battlefield/reconcile';
import { pointerToScene } from '../utils/scenePointer';

/**
 * How long after a click a press still counts as the second half of it.
 *
 * Long enough not to have to hurry, short enough that a press half a
 * second later is plainly a new gesture and measures instead.
 */
const HOLD_WINDOW_MS = 400;

/** How far a press may travel and still be a click, in pixels. */
const CLICK_SLOP = 4;

/** Alt snaps to the map's own grid - precision when you want it. */
function snap(value, tileSize, on) {
  return on && tileSize > 0 ? Math.round(value / tileSize) * tileSize : value;
}

/** This press is the marker's: the panel must not also measure or pan with it. */
function claim(e) {
  e.stopPropagation();
  e.preventDefault();
}

/** ALT IS THE EYE: "show me what this one can see". */
function lookFrom(e, tokenId, onLookFrom) {
  claim(e);
  onLookFrom?.(tokenId);
}

/** Is this press the second half of a click on the same marker - the move gesture? */
function isSecondPress(ready, tokenId, e) {
  if (!ready || ready.pressed || ready.tokenId !== tokenId) return false;
  return (
    e.timeStamp - ready.at <= HOLD_WINDOW_MS && Math.hypot(e.clientX - ready.x, e.clientY - ready.y) <= CLICK_SLOP * 3
  );
}

/**
 * The drag a hold begins: the marker, the press count, and everything the
 * whole gesture reads, captured once. (Long only as a declarative record
 * with the reason for each field beside it.)
 */
function newCarry(e, tokenId, token, count, { maxStep, routeFor, surfaceAt }) {
  // Their own stride, captured once for the drag.
  const step = maxStep?.(token) ?? Infinity;
  // Flooded ONCE, here, and reused for the whole drag: a fill per pointer
  // move would be the same answer computed sixty times a second. Null
  // means this combatant is exempt - flying, phasing, or terrain switched
  // off - and then nothing constrains them.
  const route = routeFor?.(token) ?? null;
  return {
    id: e.pointerId,
    tokenId,
    count,
    x: e.clientX,
    y: e.clientY,
    // Where they stood when picked up: the move's line, and what a refused drop puts back on the classic map.
    from: { x: token.x, z: token.z, y: token.elevation },
    // THE LIVE WORKING COPY. Two wheel notches in one frame both read the
    // same stale token out of props, and both write the same `rev` - so the
    // second is a duplicate and is dropped. Carrying the token in the drag
    // itself makes every step build on the last one, whatever React has got
    // round to rendering.
    token,
    step,
    // The storey the gesture began on. Only a fallback - but it is captured
    // ONCE, so if it is ever used the mapping stays fixed for the whole
    // drag instead of moving under it.
    planeY: token.elevation,
    // How far above the ground they are being carried. Taken from where
    // they already were, so picking a flyer up does not drop it.
    raise: Math.max(0, token.elevation - surfaceAt(token.x, token.z, token.elevation, step)),
    route,
    moved: false,
    // Exempt means exempt from the FOOTING too. Settling someone onto the
    // roof of the wall they are walking through would put them on top of
    // the very thing they are ignoring.
    free: !route,
  };
}

/**
 * What a carried marker stands on at (x, z). THE HEIGHT COMES FROM THE
 * ROUTE ITSELF where there is one (`arrived`): the fill has already
 * decided which storey each tile is walked at - asking a second function
 * the same question is how the bands and the marker came to disagree about
 * what floor somebody was on.
 */
function footingAt(d, x, z, arrived, { surfaceAt, groundAt }) {
  if (d.free) return groundAt ? groundAt(x, z) : 0;
  return arrived?.y ?? surfaceAt(x, z, d.token.elevation, d.step);
}

/**
 * Where the pointer's ground point takes the marker: the point itself for
 * an exempt mover, else wherever the route stops - or null when the route
 * refuses the tile.
 *
 * A ROUTE THAT SAYS NO IS NOT THE ABSENCE OF A ROUTE. `routeFor` returns
 * null only for someone exempt - flying, phasing, or the GM has switched
 * terrain off - and then the pointer is obeyed exactly. When a route
 * EXISTS and returns nothing, the pointer is over somewhere this combatant
 * cannot get to, and the marker must stay where it is. Treating the two
 * alike is what let a drag put a marker on top of a wall the fill had just
 * refused (v7.97.0).
 */
function routedStop(d, x, z) {
  if (!d.route) return { x, z, arrived: null };
  const stop = d.route(x, z);
  return stop ? { x: stop.x, z: stop.z, arrived: stop } : null;
}

/**
 * The scene pointer on the gesture's frozen plane, snapped when Alt is
 * held, or null off the ground.
 *
 * ONE PLANE FOR THE WHOLE GESTURE (v8.7.0). This used to unproject at the
 * marker's LIVE elevation, which every move rewrites. Step off a roof and
 * the plane moved mid-drag: `depth = (v + planeY·cos φ) / sin φ`, so the
 * same pixel began meaning a column `Δy · cot φ` metres away - 8.66 m for
 * a five-metre storey at the default three-quarter view. Where that column
 * was outside the route the drag died with no pointer motion able to
 * revive it. Frozen at the storey the gesture began on, the mapping is one
 * affine transform throughout: continuous, and recoverable when a tile is
 * refused. It is also what `groundPointFor` has always done. Every test of
 * this ran at pitch 90, where cot φ is exactly zero and none of it happens
 * - hence the pitch-30 cases in the hook's own tests.
 */
function pointerGround(e, d, camera, tileSize) {
  // The viewBox is scaled to the panel with `meet`, so a pixel is not a
  // scene unit - see pointerToScene.
  const { x: sceneX, y: sceneY } = pointerToScene(e);
  const ground = unprojectToPlane(sceneX, sceneY, d.planeY, camera, SCENE_VIEW);
  if (!ground) return null;
  return { x: snap(ground.x, tileSize, e.altKey), z: snap(ground.z, tileSize, e.altKey) };
}

/**
 * useTokenDrag - move a combatant across the ground.
 *
 * ACROSS THE GROUND, and only across it. Height used to be the other half
 * of a two-part drag - feet to walk, head to fly - which made the target
 * you needed most the smaller of the two, and changed a combatant's
 * elevation invisibly every time you missed it. Almost nobody can fly.
 * Height is the wheel now, and it is an OFFSET above whatever you are
 * standing on, so a flyer three metres up stays three metres up as it
 * crosses a trench and a roof.
 *
 * THE GESTURE IS CLICK, THEN CLICK AND HOLD. A plain drag on a marker
 * falls through to the panel and measures - magnetting to that very
 * marker, which is what makes "measure from this man" free. Moving
 * someone is the rarer and more consequential of the two acts, so it is
 * the one that asks for a deliberate gesture.
 *
 * The ground move is exact rather than approximate: `unprojectToPlane`
 * inverts the projection in closed form, so the token sits under the
 * pointer for the whole drag instead of drifting as the camera turns.
 *
 * Pointer capture, for the same reason the camera uses it: a drag that
 * ends the moment the cursor leaves the panel is not a drag.
 *
 * THE GHOST (plan design-gaps, M2): with `ghost` the drag writes nothing
 * until the release - the carried marker is `carried`, for the panel to
 * draw as a ghost along its route - and Esc lets go. The classic map
 * keeps its live drag, every step written.
 *
 * Params: camera, boundary, tokens, solids, groundAt, maxStep, routeFor,
 * writeToken, onMoved, onSelectTarget, onLookFrom, `ghost`, and
 * `stopVertical` - `(x, z, fromY, toY) => y`, floors and ceilings (see
 * `verticalStop`), and `mayPutDown` - `(token) => boolean`, a drop the map refuses (the Pinned crawl).
 */
export default function useTokenDrag(params) {
  const {
    getCamera,
    boundary,
    tokens,
    solids,
    groundAt,
    maxStep,
    routeFor,
    writeToken,
    onMoved,
    ghost = false,
  } = params;
  const drag = useRef(null);
  /** The last click on a marker, so the next press can complete the gesture. */
  const armed = useRef(null);
  const { countedClick, cancelPendingSelect } = useClickCounter(params.onSelectTarget);
  /**
   * Where the token stood when the drag began, for anything drawing the
   * route it is taking. The token itself moves live, so by the second
   * frame it no longer knows where it came from.
   */
  const [origin, setOrigin] = useState(null);
  /** The marker as it is carried, while a ghost is: where it would stand if released. */
  const [carried, setCarried] = useState(null);
  const surfaceAt = useSurfaceAt(solids, groundAt);
  // A step of the drag: the ghost's position on the display, a live write on the classic map.
  const place = useCallback((token) => (ghost ? setCarried(token) : writeToken(token)), [ghost, writeToken]);
  const refs = { drag, armed, setOrigin, cancelPendingSelect };
  const start = useCarryStart({ ...refs, tokens, surfaceAt, routeFor, maxStep, onLookFrom: params.onLookFrom });
  const move = useCarryMove({ drag, armed, getCamera, boundary, place, surfaceAt, groundAt });
  const ends = { ...refs, setCarried, writeToken, onMoved, countedClick, ghost, mayPutDown: params.mayPutDown };
  const end = useCarryEnd(ends);
  const wheel = useCarryWheel({ drag, boundary, place, surfaceAt, groundAt, stopVertical: params.stopVertical });
  useGhostEscape(ghost, origin, { drag, setOrigin, setCarried });
  return {
    onTokenPointerDown: start,
    tokenDragHandlers: { onPointerMove: move, onPointerUp: end, onPointerCancel: end },
    onTokenWheel: wheel,
    isDragging: () => drag.current !== null,
    /** `{ tokenId, x, z }` while a move is in progress, else null. */
    dragOrigin: origin,
    /** The ghost's marker while one is carried (`ghost` only), else null. */
    carried,
  };
}

/**
 * A click landed and did not travel: decide what it means.
 *
 *   1   target them - once the hold window has closed, so a hold that
 *       begins inside it (the move gesture) never targets by accident
 *   2+  target them at once: a second click that did not become a hold
 *       is not going to
 *
 * ONE CLICK, ONE OUTCOME (v8.319.0). Acting as somebody left the map - the
 * order rail and the gallery do that - so there is no third meaning to
 * wait for, and the wait that remains is the hold window itself. A drag at
 * any point cancels whatever was pending, so moving somebody never selects
 * them.
 */
function useClickCounter(onSelectTarget) {
  /** The click waiting out the hold window. */
  const pendingSelect = useRef(null);
  const cancelPendingSelect = useCallback(() => {
    if (pendingSelect.current) clearTimeout(pendingSelect.current);
    pendingSelect.current = null;
  }, []);
  const countedClick = useCallback(
    (tokenId, count) => {
      cancelPendingSelect();
      if (count >= 2) {
        onSelectTarget?.(tokenId);
        return;
      }
      pendingSelect.current = setTimeout(() => {
        pendingSelect.current = null;
        onSelectTarget?.(tokenId);
      }, HOLD_WINDOW_MS);
    },
    [cancelPendingSelect, onSelectTarget],
  );
  return { countedClick, cancelPendingSelect };
}

/**
 * The ground under a point, and therefore what a token stands on - FROM
 * WHERE THEY ARE: a crate is a step, a gantry is a ceiling. `step` comes
 * from the mover's own Size, the same number the routing uses, so a
 * Dreadnought steps over what stops a guardsman.
 */
function useSurfaceAt(solids, groundAt) {
  return useCallback(
    (x, z, from, step) => surfaceHeightAt(x, z, solids || [], groundAt ? groundAt(x, z) : 0, { from, maxStep: step }),
    [solids, groundAt],
  );
}

/** The hold begins: the marker is picked up and carried from here. */
function pickUp(e, tokenId, token, count, ctx) {
  claim(e); // do not also pull out the tape
  e.currentTarget.setPointerCapture?.(e.pointerId);
  // The hold has begun: the first click's target-in-a-moment is off.
  ctx.cancelPendingSelect();
  ctx.armed.current = null;
  // How many presses this is, carried into the drag: if it turns out not
  // to travel, that count is what it meant.
  ctx.drag.current = newCarry(e, tokenId, token, count, ctx);
  // WITH THE STOREY, not just the column. The route's own flood starts
  // here, and it needs the height this gesture began at - the token's
  // elevation changes as it is dragged, and a flood re-based on the new
  // one describes a journey nobody took.
  ctx.setOrigin({ tokenId, x: token.x, z: token.z, y: token.elevation });
}

/**
 * A press on a marker. ALT IS THE EYE: a plain click on a marker targets
 * them, so "show me what this one can see" needs a gesture of its own. A
 * first press only remembers itself (its release arms the hold) and
 * bubbles, so the panel measures with it; the second press of a click is
 * the hold, and picks the marker up.
 */
function useCarryStart(ctx) {
  const { drag, armed, setOrigin, cancelPendingSelect, tokens, surfaceAt, routeFor, maxStep, onLookFrom } = ctx;
  return useCallback(
    (tokenId, e) => {
      if (e.button !== 0 || e.shiftKey) return;
      const token = tokens[tokenId];
      if (!token) return;
      if (e.altKey) return lookFrom(e, tokenId, onLookFrom);
      const ready = armed.current;
      if (!isSecondPress(ready, tokenId, e)) {
        armed.current = { tokenId, at: e.timeStamp, x: e.clientX, y: e.clientY, pressed: true, moved: false, count: 1 };
        cancelPendingSelect();
        return;
      }
      pickUp(e, tokenId, token, ready.count + 1, {
        drag,
        armed,
        setOrigin,
        cancelPendingSelect,
        surfaceAt,
        routeFor,
        maxStep,
      });
    },
    [drag, armed, setOrigin, tokens, surfaceAt, routeFor, maxStep, cancelPendingSelect, onLookFrom],
  );
}

/**
 * A pointer move: marks a pending press as travelled (a measurement, not
 * a click), and carries the held marker. FOLLOW THE ROUTE, not the
 * pointer: a marker walks round a wall and climbs onto a crate rather than
 * passing through either, and stops where the route stops.
 */
function useCarryMove({ drag, armed, getCamera, boundary, place, surfaceAt, groundAt }) {
  return useCallback(
    (e) => {
      const held = armed.current;
      if (held?.pressed && Math.hypot(e.clientX - held.x, e.clientY - held.y) > CLICK_SLOP) held.moved = true;
      const d = drag.current;
      if (!d || d.id !== e.pointerId || d.letGo) return;
      const ground = pointerGround(e, d, getCamera(), boundary.tileSize);
      const stop = ground && routedStop(d, ground.x, ground.z);
      if (!stop) return;
      const placed = moveToken(d.token, stop.x, stop.z, boundary);
      d.moved = true;
      const footing = footingAt(d, placed.x, placed.z, stop.arrived, { surfaceAt, groundAt });
      d.token = { ...placed, elevation: footing + d.raise };
      place(d.token);
    },
    [drag, armed, getCamera, boundary, place, surfaceAt, groundAt],
  );
}

/**
 * A press released. A press that did not travel is a click, and arms the
 * hold; one that did travel was a measurement, and arms nothing - and a
 * click is a target, once the hold window has closed. A carried marker is
 * put down (the last position flushed at once), or, if it never travelled,
 * the hold was just the nth click on it.
 */
function useCarryEnd({
  drag,
  armed,
  setOrigin,
  setCarried,
  cancelPendingSelect,
  writeToken,
  onMoved,
  countedClick,
  mayPutDown,
  ghost,
}) {
  return useCallback(
    (e) => {
      releasePress(armed, e, countedClick);
      const d = drag.current;
      if (!d || d.id !== e.pointerId) return;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      drag.current = null;
      setOrigin(null);
      setCarried(null);
      // Let go of with Esc (the ghost): neither a move nor a click. Nor a drop the map refuses (the Pinned crawl
      // outside its reach): the ghost goes back and nothing is written.
      if (d.letGo) return;
      if (d.moved && mayPutDown && !mayPutDown(d.token)) {
        // The classic map wrote each step as it went: put the marker back where it was picked up (the review).
        if (!ghost) writeToken({ ...d.token, x: d.from.x, z: d.from.z, elevation: d.from.y }, { immediate: true });
        return;
      }
      if (d.moved) {
        putDown(d, writeToken, onMoved);
        // It was a MOVE, so it was not a click at all.
        cancelPendingSelect();
        return;
      }
      // A second click targets at once. Re-armed, so a hold can still follow.
      countedClick(d.tokenId, d.count);
      armed.current = {
        tokenId: d.tokenId,
        at: e.timeStamp,
        x: d.x,
        y: d.y,
        pressed: false,
        moved: false,
        count: d.count,
      };
    },
    [drag, armed, setOrigin, setCarried, writeToken, onMoved, countedClick, cancelPendingSelect, mayPutDown, ghost],
  );
}

/** A pending press released: a click arms the hold (and targets, in time); a measurement arms nothing. */
function releasePress(armed, e, countedClick) {
  const held = armed.current;
  if (!held?.pressed) return;
  armed.current = held.moved ? null : { ...held, at: e.timeStamp, pressed: false };
  if (!held.moved && held.count === 1) countedClick(held.tokenId, 1);
}

/**
 * The end of a move: flush the debounce - the last position of a drag must
 * not wait (the ghost's one and only write) - and report it WITH THE
 * STOREYS IT CROSSED and where it began and ended. The panel cannot work
 * this out afterwards: the route is gone by the time the drag ends, and
 * the token's own elevation only says where it finished.
 */
function putDown(d, writeToken, onMoved) {
  const { x, z, elevation } = d.token;
  // The line first, then the token: the walk is read off the line, and a screen that sees the token before it
  // holds the figure where it stood (useDisplayWalks) - this order shortens that wait (the Track M review).
  onMoved?.(d.tokenId, { fromY: d.planeY, toY: elevation, from: d.from, to: { x, z, y: elevation } });
  writeToken(d.token, { immediate: true });
}

/**
 * ESC LETS GO OF THE GHOST (plan design-gaps, M2): the ghost and its route
 * vanish at once and the carry is marked let go, so the release that always
 * follows ends it through the one end path (useCarryEnd) writing nothing -
 * the marker never left. Only while a ghost is carried; the classic map's
 * live drag has already written its steps.
 */
function useGhostEscape(ghost, origin, { drag, setOrigin, setCarried }) {
  const letGo = useCallback(() => {
    const d = drag.current;
    if (!d) return;
    d.letGo = true;
    setOrigin(null);
    setCarried(null);
  }, [drag, setOrigin, setCarried]);
  useEscapeKey(ghost && !!origin, letGo);
}

/**
 * The wheel, while carrying someone, is their height.
 *
 * Returns true when it took the event, so the panel knows not to zoom.
 * One tile per notch, and never below the ground they stand on. CLAMPED BY
 * WHAT IS THERE: the wheel used to pass straight through a gantry, so
 * whether somebody stood under a platform or on top of it came down to how
 * far it had been turned. Terrain-off keeps bypassing this, as it bypasses
 * everything else.
 */
function useCarryWheel({ drag, boundary, place, surfaceAt, groundAt, stopVertical }) {
  return useCallback(
    (e) => {
      const d = drag.current;
      if (!d) return false;
      const step = boundary.tileSize > 0 ? boundary.tileSize : 1;
      d.raise = Math.max(0, d.raise + (e.deltaY < 0 ? step : -step));
      d.moved = true;
      const { x, z, elevation } = d.token;
      const footing = footingAt(d, x, z, null, { surfaceAt, groundAt });
      const wanted = footing + d.raise;
      const stopped = d.free ? wanted : (stopVertical?.(x, z, elevation, wanted) ?? wanted);
      d.raise = Math.max(0, stopped - footing);
      d.token = { ...d.token, elevation: stopped, rev: d.token.rev + 1 };
      place(d.token);
      return true;
    },
    [drag, boundary, place, surfaceAt, groundAt, stopVertical],
  );
}
